import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Notice, Spinner } from "@/components/common/States";
import { api, errorMessage } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";

const EMPTY = {
  full_name: "", role: "", nisn: "", nip: "",
  student_registration_number: "", employee_registration_number: "",
  reason: "",
};
const STAFF_ROLES = new Set(["kepala_sekolah", "operator", "guru", "wali_kelas"]);

export const AddMemberDialog = ({ open, onOpenChange, roles, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [err, setErr] = useState("");
  const [identity, setIdentity] = useState(null);

  const student = f.role === "siswa";
  const staff = STAFF_ROLES.has(f.role);
  const employeeRole = staff;
  const identityKind = student ? "nisn" : staff ? "nip" : null;
  const identityValue = student ? f.nisn.trim() : staff ? f.nip.trim() : "";
  const identityComplete = identityKind === "nisn"
    ? /^\d{10}$/.test(identityValue)
    : identityKind === "nip"
      ? /^\d{18}$/.test(identityValue)
      : false;

  const set = (k) => (e) => {
    const value = e.target.value;
    setF((prev) => ({ ...prev, [k]: value }));
    setErr("");
  };

  useEffect(() => {
    setIdentity(null);
    if (!open || !identityKind || !identityComplete) {
      setLookupBusy(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookupBusy(true);
      try {
        const { data } = await api.get("/school/members/identity-lookup", {
          params: { kind: identityKind, value: identityValue },
        });
        if (!cancelled) {
          setIdentity({ ...data, _kind: identityKind, _value: identityValue });
        }
      } catch (e) {
        if (!cancelled) {
          setIdentity({
            lookup_error: errorMessage(e),
            _kind: identityKind,
            _value: identityValue,
          });
        }
      } finally {
        if (!cancelled) setLookupBusy(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, identityKind, identityValue, identityComplete]);

  const changeRole = (role) => {
    setF((prev) => ({
      ...prev,
      role,
      nisn: "",
      nip: "",
      student_registration_number: "",
      employee_registration_number: "",
    }));
    setIdentity(null);
    setErr("");
  };

  const submit = async () => {
    if (f.full_name.trim().length < 3) return setErr("Nama lengkap minimal 3 karakter.");
    if (!f.role) return setErr("Pilih peran anggota.");
    if (student && !/^\d{10}$/.test(f.nisn.trim())) return setErr("NISN wajib diisi dengan 10 digit angka.");
    if (staff && !/^\d{18}$/.test(f.nip.trim())) return setErr("NIP wajib diisi dengan 18 digit angka.");
    if (identityChecking) return setErr("Tunggu hingga pemeriksaan identitas selesai.");
    if (identityMatchesCurrentInput && identity?.lookup_error) return setErr("Pencarian identitas gagal. Silakan coba lagi.");
    if (identity?.found && identity.already_member) {
      return setErr("Identitas ini sudah menjadi anggota sekolah Anda.");
    }

    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/school/members", {
        full_name: f.full_name.trim(),
        role: f.role,
        reason: f.reason.trim() || null,
        nisn: student ? f.nisn.trim() : null,
        nip: staff ? f.nip.trim() : null,
        student_registration_number: student
          ? (f.student_registration_number.trim() || null)
          : null,
        employee_registration_number: employeeRole
          ? (f.employee_registration_number.trim() || null)
          : null,
      });
      onDone(data);
      setF(EMPTY);
      setIdentity(null);
      onOpenChange(false);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const identityMatchesCurrentInput = Boolean(
    identity &&
    identity._kind === identityKind &&
    identity._value === identityValue
  );
  const identityChecking = Boolean(
    open && identityKind && identityComplete &&
    (lookupBusy || !identityMatchesCurrentInput)
  );
  const identityBusyOrBlocked = identityChecking ||
    Boolean(identityMatchesCurrentInput && identity?.found && identity.already_member);

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (!next && busy) return;
      if (!next) {
        setF(EMPTY);
        setIdentity(null);
        setErr("");
      }
      onOpenChange(next);
    }}>
      <DialogContent data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle>Tambah anggota</DialogTitle>
          <DialogDescription>
            Identitas yang ditemukan di Registri Identitas Global dan belum menjadi anggota sekolah akan diajukan untuk ditinjau Admin Platform. Identitas yang sudah menjadi anggota tidak dapat ditambahkan ulang.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="m-name">Nama lengkap</Label>
            <Input id="m-name" value={f.full_name} onChange={set("full_name")} data-testid="member-name-input" />
          </div>

          <div className="space-y-2">
            <Label>Peran</Label>
            <Select value={f.role} onValueChange={changeRole}>
              <SelectTrigger aria-label="Peran" data-testid="member-role-select">
                <SelectValue placeholder="Pilih peran" />
              </SelectTrigger>
              <SelectContent>
                {roles.filter((r) => r !== "wali_kelas").map((r) => (
                  <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {student && (
            <div className="space-y-2">
              <Label htmlFor="m-nisn">NISN (wajib, 10 digit)</Label>
              <Input
                id="m-nisn"
                inputMode="numeric"
                maxLength={10}
                value={f.nisn}
                onChange={set("nisn")}
                data-testid="member-nisn-input"
              />
            </div>
          )}

          {staff && (
            <div className="space-y-2">
              <Label htmlFor="m-nip">NIP (wajib, 18 digit)</Label>
              <Input
                id="m-nip"
                inputMode="numeric"
                maxLength={18}
                value={f.nip}
                onChange={set("nip")}
                data-testid="member-nip-input"
              />
            </div>
          )}

          {student && (
            <div className="space-y-2">
              <Label htmlFor="m-student-registration">NIS/NIPD/NISM/NISD (opsional)</Label>
              <Input
                id="m-student-registration"
                value={f.student_registration_number}
                onChange={set("student_registration_number")}
                maxLength={50}
                data-testid="member-student-registration-input"
              />
            </div>
          )}

          {employeeRole && (
            <div className="space-y-2">
              <Label htmlFor="m-employee-registration">NUPTK/NITK/NRG (opsional)</Label>
              <Input
                id="m-employee-registration"
                value={f.employee_registration_number}
                onChange={set("employee_registration_number")}
                maxLength={50}
                data-testid="member-employee-registration-input"
              />
            </div>
          )}

          {identityKind && identityValue && !identityComplete && (
            <Notice tone="info" testId="identity-format-notice">
              {student ? "Masukkan NISN lengkap sebanyak 10 digit." : "Masukkan NIP lengkap sebanyak 18 digit."}
            </Notice>
          )}

          {identityChecking && (
            <Notice tone="info" testId="identity-lookup-loading">
              <Spinner /> Memeriksa identitas...
            </Notice>
          )}

          {identityMatchesCurrentInput && identity?.lookup_error && (
            <Notice tone="danger" testId="identity-lookup-error">
              {identity.lookup_error}
            </Notice>
          )}

          {identityMatchesCurrentInput && !identity.lookup_error && identity.found && (
            <Notice tone={identity.already_member ? "danger" : "warn"} testId="identity-found-notice">
              <div className="space-y-1">
                <p className="font-semibold">Identitas ditemukan di Registri Identitas Global.</p>
                <p>Nama terdaftar: {identity.full_name || "Tidak tersedia"}</p>
                <p>Status verifikasi: {identity.verification_status || "unverified"}</p>
                {identity.already_member
                  ? <p>Identitas ini sudah menjadi anggota sekolah Anda. Penambahan diblokir.</p>
                  : <p>Pengajuan akan dikirim ke tinjauan Admin Platform dan belum menjadi anggota sampai ditinjau.</p>}
              </div>
            </Notice>
          )}

          {identityMatchesCurrentInput && !identity.lookup_error && !identity.found && (
            <Notice tone="info" testId="identity-not-found-notice">
              Identitas belum ditemukan. Data baru dapat diajukan sebagai identitas baru.
            </Notice>
          )}

          {f.role === "orang_tua" && (
            <Notice tone="info" testId="guardian-identity-notice">
              Untuk peran orang tua/wali, NISN dan NIP tidak diminta pada form ini.
            </Notice>
          )}

          {f.role === "pegawai_non_asn" && (
            <Notice tone="info" testId="non-asn-identity-notice">
              Pegawai Non-ASN tidak memerlukan NIP atau NISN pada form ini.
            </Notice>
          )}

          <div className="space-y-2">
            <Label htmlFor="m-reason">Catatan/alasan (opsional)</Label>
            <Input id="m-reason" value={f.reason} onChange={set("reason")} data-testid="member-reason-input" />
          </div>

          {err && <Notice tone="danger" testId="add-member-error">{err}</Notice>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="add-member-cancel-button">
            Batal
          </Button>
          <Button
            onClick={submit}
            disabled={busy || identityBusyOrBlocked}
            data-testid="add-member-submit-button"
          >
            {busy && <Spinner />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
